import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, MessageCircle } from "lucide-react";
import { submitConsultation } from "@/lib/api";
import { waLink, ADMIN_WA_PHONE } from "@/lib/wa";
import { trackConversion, trackClick, trackEvent, getUserId, setUserProperty } from "@/lib/analytics";

const ROOMS = ["Ruang Tamu", "Kamar Tidur", "Dapur", "Kamar Mandi", "Seluruh Rumah", "Kantor", "Café / Komersial"];
const BUDGETS = ["< 50 juta", "50 - 150 juta", "150 - 500 juta", "> 500 juta"];

export default function Consultation() {
  const [form, setForm] = useState({
    nama: "",
    email: "",
    telepon: "",
    jenis_ruangan: ROOMS[0],
    luas_ruangan: "",
    budget: BUDGETS[0],
    style_preference: "",
    catatan: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Fire form_start once when user first interacts with any field
  const onFieldFocus = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent("consultation_form_start", { user_id: getUserId() });
  };

  // Track form abandonment (partial fill without submit) on unmount / navigation
  useEffect(() => {
    const beforeUnload = () => {
      if (startedRef.current && !done) {
        const filled = ["nama", "email", "telepon"].filter((k) => form[k]).length;
        trackEvent("consultation_form_abandon", { filled_fields: filled, user_id: getUserId() });
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [done, form]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nama || !form.email || !form.telepon) {
      toast.error("Mohon lengkapi nama, email, dan telepon");
      trackEvent("consultation_submit_validation_error");
      return;
    }
    setLoading(true);
    try {
      const uid = getUserId();
      await submitConsultation({ ...form, analytics_user_id: uid });
      setDone(true);
      toast.success("Permintaan konsultasi berhasil dikirim!");
      setUserProperty("consultation_submitted", "true");
      trackConversion("consultation_submit", {
        room_type: form.jenis_ruangan,
        budget: form.budget,
        has_style: form.style_preference.length > 0,
        has_notes: form.catatan.length > 0,
        user_id: uid,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Gagal mengirim. Coba lagi.");
      trackClick("consultation_submit_error");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const waMsg = `Halo Admin AI AT Studio, saya ${form.nama} ingin konsultasi desain untuk ${form.jenis_ruangan}. Budget: ${form.budget}. Nomor saya: ${form.telepon}.`;
    const adminWa = waLink(ADMIN_WA_PHONE, waMsg);
    return (
      <section id="konsultasi" data-testid="consultation-section" className="relative py-24 lg:py-32 px-6 lg:px-12 bg-gradient-to-b from-sky-50/40 to-white">
        <div className="max-w-3xl mx-auto text-center fade-up" data-testid="consultation-success">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-sky-100 to-cyan-50 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={40} className="text-sky-600" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tighter mb-6 text-slate-900">
            Terima kasih, <span className="italic text-sky-600">{form.nama.split(" ")[0]}.</span>
          </h2>
          <p className="text-slate-600 mb-8 font-light leading-relaxed">
            Tim desainer kami akan menghubungi Anda dalam <span className="text-slate-900 font-medium">1 × 24 jam</span> melalui
            email atau WhatsApp — <b>termasuk detail ukuran & kuantitas material</b> yang presisi untuk ruangan Anda.
            Konfirmasi sudah kami kirim ke <b>{form.email}</b>.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={adminWa}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackClick("consultation_wa_admin")}
              data-testid="consultation-wa-admin"
              className="inline-flex items-center justify-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full shadow-lg shadow-emerald-200 transition-colors"
            >
              <MessageCircle size={16} /> Lanjut Chat di WhatsApp
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="konsultasi" data-testid="consultation-section" className="relative py-24 lg:py-32 px-6 lg:px-12 bg-gradient-to-b from-sky-50/40 to-white">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4 font-semibold">Konsultasi Gratis</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-tight mb-8 text-slate-900">
            Ceritakan ruangan<br /><span className="italic text-sky-600">impian</span> Anda.
          </h2>
          <p className="text-slate-600 font-light leading-relaxed mb-8">
            Isi formulir berikut dan tim desainer profesional kami akan menghubungi Anda untuk
            konsultasi mendalam. Tanpa biaya, tanpa komitmen.
          </p>
          <ul className="space-y-3 text-sm text-slate-700">
            {["Respon dalam 1×24 jam", "Konfirmasi otomatis via email", "Pre-fill WhatsApp ke nomor Anda", "Tim desainer bersertifikasi"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="w-2 h-2 bg-sky-500 rounded-full" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={onSubmit} onFocus={onFieldFocus} className="lg:col-span-7 bg-white border border-sky-100 rounded-3xl p-8 lg:p-10 shadow-xl shadow-sky-100/40 space-y-6" data-testid="consultation-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Nama Lengkap*">
              <input required value={form.nama} onChange={(e) => set("nama", e.target.value)} className="input-mod" data-testid="input-nama" />
            </Field>
            <Field label="Email*">
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-mod" data-testid="input-email" />
            </Field>
            <Field label="No. Telepon / WA*">
              <input required value={form.telepon} onChange={(e) => set("telepon", e.target.value)} placeholder="08xxxxxxxxxx" className="input-mod" data-testid="input-telepon" />
            </Field>
            <Field label="Luas Ruangan (m²)">
              <input value={form.luas_ruangan} onChange={(e) => set("luas_ruangan", e.target.value)} placeholder="ex: 36" className="input-mod" data-testid="input-luas" />
            </Field>
            <Field label="Jenis Ruangan">
              <select value={form.jenis_ruangan} onChange={(e) => set("jenis_ruangan", e.target.value)} className="input-mod" data-testid="input-jenis-ruangan">
                {ROOMS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Budget">
              <select value={form.budget} onChange={(e) => set("budget", e.target.value)} className="input-mod" data-testid="input-budget">
                {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Preferensi Gaya">
            <input value={form.style_preference} onChange={(e) => set("style_preference", e.target.value)} placeholder="ex: Bathroom klasik modern dengan marble Carrara" className="input-mod" data-testid="input-style" />
          </Field>
          <Field label="Catatan Tambahan">
            <textarea rows={4} value={form.catatan} onChange={(e) => set("catatan", e.target.value)} className="input-mod resize-none" data-testid="input-catatan" />
          </Field>

          <button
            type="submit"
            disabled={loading}
            data-testid="consultation-submit"
            className="w-full inline-flex items-center justify-center gap-3 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 disabled:opacity-50 text-white font-semibold text-xs uppercase tracking-[0.2em] px-8 py-5 rounded-full shadow-lg shadow-sky-200 transition-colors"
          >
            <Send size={16} /> {loading ? "Mengirim…" : "Kirim Permintaan Konsultasi"}
          </button>
        </form>
      </div>

      <style>{`
        .input-mod {
          width: 100%;
          background: #F8FBFE;
          border: 1px solid #E2E8F0;
          border-radius: 12px;
          color: #0F172A;
          padding: 12px 16px;
          font-size: 15px;
          outline: none;
          transition: all 0.2s ease;
        }
        .input-mod:focus { border-color: #0EA5E9; background: white; box-shadow: 0 0 0 3px rgba(14,165,233,0.1); }
        .input-mod::placeholder { color: #94A3B8; }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2 block">{label}</span>
      {children}
    </label>
  );
}
